import { ArrowLeft, Calendar, Clock, User, CheckCircle, Gift, Heart, Share2, MessageCircle, Star, List, ShoppingBag, AlertCircle, ThumbsUp, Smartphone } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface BlogPost2Props {
    onBack: () => void;
}

export function BlogPost2({ onBack }: BlogPost2Props) {
    const { t, tObject } = useTranslation();

    const meta = tObject('blogPost2.meta') as any;
    const intro = tObject('blogPost2.intro') as any;
    const section1 = tObject('blogPost2.section1') as any;
    const section2 = tObject('blogPost2.section2') as any;
    const section3 = tObject('blogPost2.section3') as any;
    const section4 = tObject('blogPost2.section4') as any;
    const section5 = tObject('blogPost2.section5') as any;
    const section6 = tObject('blogPost2.section6') as any;
    const section7 = tObject('blogPost2.section7') as any;
    const section8 = tObject('blogPost2.section8') as any;
    const section9 = tObject('blogPost2.section9') as any;
    const section10 = tObject('blogPost2.section10') as any;
    const cta = tObject('blogPost2.cta') as any;

    return (
        <div className="min-h-screen bg-white pt-24 pb-12">
            <div className="relative h-[500px] w-full overflow-hidden mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-700 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1512418490979-92798cec1380?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                    <span className="px-4 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                        {meta?.badge}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold max-w-4xl mb-6 leading-tight drop-shadow-lg">
                        {meta?.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base opacity-90">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{meta?.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{meta?.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{meta?.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-8 font-medium"
                >
                    <div className="p-2 bg-gray-100 rounded-full group-hover:bg-blue-50 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    {meta?.backButton}
                </button>

                <article className="prose prose-lg max-w-none">
                    <p className="lead text-2xl text-gray-600 mb-10 font-light leading-relaxed border-l-4 border-blue-500 pl-6 italic">
                        {intro?.lead}
                    </p>

                    <div className="bg-blue-50 rounded-2xl p-8 mb-12 border border-blue-100">
                        <h3 className="text-blue-800 font-bold text-xl mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 fill-current" />
                            {intro?.learningTitle}
                        </h3>
                        <ul className="grid md:grid-cols-2 gap-3 list-none p-0 m-0">
                            {intro?.learningItems?.map((item: string, i: number) => (
                                <li key={i} className="flex items-center gap-2 text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-lg text-xl font-bold">1</span>
                        {section1?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section1?.p1}</p>
                    <p className="text-gray-700 mb-6">{section1?.p2}</p>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-purple-600 text-white rounded-lg text-xl font-bold">2</span>
                        {section2?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section2?.p1}</p>
                    <p className="text-gray-700 mb-6">{section2?.p2}</p>
                    <div className="bg-white border-l-4 border-purple-500 shadow-sm p-6 mb-8">
                        <h4 className="font-bold text-purple-800 mb-2">{section2?.budgetExample?.title}</h4>
                        <ul className="list-none space-y-4 m-0">
                            <li className="flex items-start gap-2">
                                <span className="w-3 h-3 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                                <span><strong>{section2?.budgetExample?.low?.split(':')[0]}:</strong> {section2?.budgetExample?.low?.split(':').slice(1).join(':')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="w-3 h-3 bg-yellow-400 rounded-full mt-1.5 flex-shrink-0"></span>
                                <span><strong>{section2?.budgetExample?.medium?.split(':')[0]}:</strong> {section2?.budgetExample?.medium?.split(':').slice(1).join(':')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="w-3 h-3 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                                <span><strong>{section2?.budgetExample?.high?.split(':')[0]}:</strong> {section2?.budgetExample?.high?.split(':').slice(1).join(':')}</span>
                            </li>
                        </ul>
                    </div>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-indigo-600 text-white rounded-lg text-xl font-bold">3</span>
                        {section3?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section3?.p1}</p>
                    <p className="text-gray-700 mb-6">{section3?.p2}</p>
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                            <h4 className="font-bold text-red-700 mb-2 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> {section3?.wrong?.title}</h4>
                            <p className="text-gray-600 text-sm mb-3">{section3?.wrong?.example}</p>
                            <p className="text-gray-700 text-sm">
                                <strong>{t('blogPost2.section3.wrong.why').split(':')[0]}:</strong> {t('blogPost2.section3.wrong.why').split(':').slice(1).join(':')}
                            </p>
                        </div>
                        <div className="bg-green-50 p-6 rounded-xl border border-green-100">
                            <h4 className="font-bold text-green-700 mb-2 flex items-center gap-2"><ThumbsUp className="w-5 h-5" /> {section3?.right?.title}</h4>
                            <p className="text-gray-600 text-sm mb-3">{section3?.right?.example}</p>
                            <p className="text-gray-700 text-sm">
                                <strong>{t('blogPost2.section3.right.why').split(':')[0]}:</strong> {t('blogPost2.section3.right.why').split(':').slice(1).join(':')}
                            </p>
                        </div>
                    </div>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-lg text-xl font-bold">4</span>
                        {section4?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section4?.p1}</p>
                    <p className="text-gray-700 mb-6">{section4?.p2}</p>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-orange-500 text-white rounded-lg text-xl font-bold">5</span>
                        {section5?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section5?.p1}</p>
                    <p className="text-gray-700 mb-4">{section5?.p2}</p>
                    <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-3">
                        <li><strong>{t('blogPost2.section5.restrictions.allergies').split(':')[0]}:</strong> {t('blogPost2.section5.restrictions.allergies').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost2.section5.restrictions.dietary').split(':')[0]}:</strong> {t('blogPost2.section5.restrictions.dietary').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost2.section5.restrictions.sizes').split(':')[0]}:</strong> {t('blogPost2.section5.restrictions.sizes').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost2.section5.restrictions.preferences').split(':')[0]}:</strong> {t('blogPost2.section5.restrictions.preferences').split(':').slice(1).join(':')}</li>
                    </ul>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-pink-600 text-white rounded-lg text-xl font-bold">6</span>
                        {section6?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section6?.intro}</p>
                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                        <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-all transform hover:-translate-y-1">
                            <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <ShoppingBag className="w-7 h-7" />
                            </div>
                            <h4 className="font-bold text-lg mb-3">{section6?.categories?.fashion?.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{section6?.categories?.fashion?.description}</p>
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-all transform hover:-translate-y-1">
                            <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Smartphone className="w-7 h-7" />
                            </div>
                            <h4 className="font-bold text-lg mb-3">{section6?.categories?.tech?.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{section6?.categories?.tech?.description}</p>
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-all transform hover:-translate-y-1">
                            <div className="w-14 h-14 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Gift className="w-7 h-7" />
                            </div>
                            <h4 className="font-bold text-lg mb-3">{section6?.categories?.home?.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{section6?.categories?.home?.description}</p>
                        </div>
                    </div>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-cyan-600 text-white rounded-lg text-xl font-bold">7</span>
                        {section7?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section7?.p1}</p>
                    <p className="text-gray-700 mb-6">{section7?.p2}</p>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-rose-600 text-white rounded-lg text-xl font-bold">8</span>
                        {section8?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section8?.p1}</p>
                    <p className="text-gray-700 mb-6">{section8?.p2}</p>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-gray-800 text-white rounded-lg text-xl font-bold">9</span>
                        {section9?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section9?.p1}</p>
                    <blockquote className="border-l-4 border-gray-300 pl-6 italic text-gray-600 my-6 py-2 bg-gray-50 rounded-r-lg">
                        {section9?.example}
                    </blockquote>
                    <p className="text-gray-700 mb-6">{section9?.p2}</p>

                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-emerald-600 text-white rounded-lg text-xl font-bold">10</span>
                        {section10?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{section10?.p1}</p>
                    <p className="text-gray-700 mb-6">{section10?.p2}</p>

                    <hr className="my-12 border-gray-200" />

                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white text-center shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-full bg-white/5 pointer-events-none"></div>
                        <List className="w-20 h-20 mx-auto mb-6 text-white animate-bounce drop-shadow-md" />
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">{cta?.title}</h2>
                        <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
                            {cta?.description}
                        </p>
                        <button
                            onClick={onBack}
                            className="bg-white text-blue-600 px-10 py-5 rounded-full font-bold text-lg hover:bg-gray-100 hover:scale-105 transition-all shadow-xl uppercase tracking-wide"
                        >
                            {cta?.button}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}